import { Button } from 'primereact/button'
import { Dialog } from 'primereact/dialog'
import { Divider } from 'primereact/divider'
import { Image } from 'primereact/image'
import './anpr-event-details.scss'
import { ANPREvent } from 'domain/anpr-event'
import MercosulPlate from 'shared/mercosul-plate/mercosul-license-plate'
import { isSpeedViolation } from '../util/speed-violation-filter'
import { vehicleTypes } from '../types/consts'
import { isLowConfidence } from '../utils/confidence-level'

export interface EventDetailsViewerProps {
  anprEvent: ANPREvent
  visible: boolean
  hideDialog: () => void
  setSelectedImage: (selectedImage: string | null) => void
}

const vehicleTypeLabel = (value: string): string =>
  vehicleTypes.find((t) => t.value === value)?.label ?? value

const colorMap: { [key: string]: string } = {
  white: 'Branco',
  black: 'Preto',
  silver: 'Prata',
  blue: 'Azul',
  red: 'Vermelho',
  green: 'Verde',
  yellow: 'Amarelo',
  gray: 'Cinza',
  brown: 'Marrom',
  unknown: 'Desconhecido',
  purple: 'Roxo',
}

const getVehicleColorBadge = (vehicleColor: string) => {
  const displayColor = colorMap[vehicleColor] || vehicleColor || 'Não identificado'
  return (
    <span className={`events-color-badge events-color-${vehicleColor || 'unknown'}`}>
      <i className='pi pi-circle-fill' />
      {displayColor}
    </span>
  )
}

const EventDetailViewer = (props: EventDetailsViewerProps) => {
  const { anprEvent } = props
  const violation = isSpeedViolation(anprEvent)
  const hasEventInfo = !!(anprEvent.eventName || anprEvent.eventDescription)

  const footer = (
    <div className='events-dialog-footer'>
      <Button label='Fechar' icon='pi pi-times' outlined className='events-btn-close' onClick={props.hideDialog} />
    </div>
  )

  return (
    <Dialog
      visible={props.visible}
      key={anprEvent.licensePlate}
      style={{ width: '700px' }}
      header={
        <div className='events-dialog-header'>
          <i className='pi pi-info-circle events-dialog-icon' />
          <span>Detalhes do Evento</span>
        </div>
      }
      modal
      className='events-dialog'
      footer={footer}
      onHide={props.hideDialog}
    >
      <div className='events-details'>
        <div className='events-details-grid'>

          {/* ── Seção 1: Veículo ── */}
          <div className='events-detail-section'>
            <h4 className='events-section-title'>
              <i className='pi pi-car' />
              Informações do Veículo
            </h4>
            <Divider />

            <div className='events-detail-row'>
              <div className='events-detail-item'>
                <MercosulPlate licensePlate={anprEvent.licensePlate} size='M' key={anprEvent.licensePlate} />
              </div>

              {anprEvent.confidenceLevel !== null && (
                <div className='events-detail-item'>
                  <label
                    className='events-detail-label'
                    style={isLowConfidence(anprEvent.confidenceLevel) ? { color: 'var(--yellow-500)' } : undefined}
                  >
                    <i className='pi pi-question-circle' />
                    Confiança da Leitura
                  </label>
                  <span
                    className='events-detail-value'
                    style={isLowConfidence(anprEvent.confidenceLevel) ? { color: 'var(--yellow-500)', fontWeight: 600 } : undefined}
                  >
                    {anprEvent.confidenceLevel}%
                    {isLowConfidence(anprEvent.confidenceLevel) && ' — vale conferir'}
                  </span>
                </div>
              )}

              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-palette' />
                  Cor do Veículo
                </label>
                <span className='events-detail-value'>
                  {getVehicleColorBadge(anprEvent.vehicleColor)}
                </span>
              </div>
            </div>

            <div className='events-detail-row'>
              {anprEvent.vehicleType && (
                <div className='events-detail-item'>
                  <label className='events-detail-label'>
                    <i className='pi pi-car' />
                    Tipo de Veículo
                  </label>
                  <span className='events-detail-value'>
                    {vehicleTypeLabel(anprEvent.vehicleType)}
                  </span>
                </div>
              )}

              {anprEvent.direction && (
                <div className='events-detail-item'>
                  <label className='events-detail-label'>
                    <i className='pi pi-arrows-h' />
                    Direção
                  </label>
                  <span className='events-detail-value'>{anprEvent.direction}</span>
                </div>
              )}
            </div>
          </div>

          {/* ── Seção 2: Câmera & Velocidade ── */}
          <div className='events-detail-section'>
            <h4 className='events-section-title'>
              <i className='pi pi-video' />
              Câmera & Velocidade
            </h4>
            <Divider />

            <div className='events-detail-row'>
              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-video' />
                  Câmera
                </label>
                <span className='events-detail-value'>{anprEvent.cameraName}</span>
              </div>

              <div className='events-detail-item'>
                <label className='events-detail-label'>
                  <i className='pi pi-calendar' />
                  Data de Detecção
                </label>
                <span className='events-detail-value'>
                  {new Date(anprEvent.createdDate).toLocaleString('pt-BR')}
                </span>
              </div>
            </div>

            <div className='events-detail-row'>
              <div className='events-detail-item'>
                <label className='events-detail-label' style={violation ? { color: 'var(--red-500)' } : undefined}>
                  <i className='pi pi-gauge' />
                  Velocidade
                </label>
                <span className='events-speed-large' style={violation ? { color: 'var(--red-500)' } : undefined}>
                  {anprEvent.speed || 0} km/h
                </span>
              </div>

              {anprEvent.cameraSpeedLimit > 0 && (
                <div className='events-detail-item'>
                  <label className='events-detail-label'>
                    <i className='pi pi-map-marker' />
                    Limite da Câmera
                  </label>
                  <span className='events-speed-large'>
                    {anprEvent.cameraSpeedLimit} km/h
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* ── Seção 2b: Infração (só quando a câmera classificou uma) ── */}
          {anprEvent.illegalName && (
            <div className='events-detail-section events-detail-section--illegal'>
              <h4 className='events-section-title events-section-title--illegal'>
                <i className='pi pi-exclamation-triangle' />
                Infração Detectada
              </h4>
              <Divider />

              <div className='events-detail-row'>
                <div className='events-detail-item'>
                  <label className='events-detail-label'>
                    <i className='pi pi-tag' />
                    Tipo
                  </label>
                  <span className='events-detail-value'>{anprEvent.illegalName}</span>
                </div>

                {anprEvent.illegalDescription && (
                  <div className='events-detail-item'>
                    <label className='events-detail-label'>
                      <i className='pi pi-align-left' />
                      Descrição
                    </label>
                    <span className='events-detail-value'>{anprEvent.illegalDescription}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Seção 3: Evento (somente se houver dados) ── */}
          {hasEventInfo && (
            <div className='events-detail-section'>
              <h4 className='events-section-title'>
                <i className='pi pi-tag' />
                Informações do Evento
              </h4>
              <Divider />

              <div className='events-detail-row'>
                {anprEvent.eventName && (
                  <div className='events-detail-item'>
                    <label className='events-detail-label'>
                      <i className='pi pi-tag' />
                      Nome do Evento
                    </label>
                    <span className='events-detail-value'>{anprEvent.eventName}</span>
                  </div>
                )}

                {anprEvent.eventDescription && (
                  <div className='events-detail-item'>
                    <label className='events-detail-label'>
                      <i className='pi pi-align-left' />
                      Descrição
                    </label>
                    <span className='events-detail-value'>{anprEvent.eventDescription}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* ── Seção 4: Imagens ── */}
          <div className='events-detail-section'>
            <h4 className='events-section-title'>
              <i className='pi pi-image' />
              Imagens Capturadas
            </h4>
            <Divider />

            <div className='events-images-grid'>
              <div className='events-image-detail'>
                <label className='events-image-label'>Imagem da Placa</label>
                {anprEvent.licensePlateImagePath ? (
                  <div className='events-image-preview' onClick={() => props.setSelectedImage(anprEvent.licensePlateImagePath)}>
                    <Image src={anprEvent.licensePlateImagePath} alt='Placa' className='events-preview-image' width='200px' />
                    <div className='events-preview-overlay'>
                      <i className='pi pi-search-plus' />
                      <span>Clique para ampliar</span>
                    </div>
                  </div>
                ) : (
                  <div className='events-no-image-large'>
                    <i className='pi pi-image' />
                    <span>Imagem não disponível</span>
                  </div>
                )}
              </div>

              <div className='events-image-detail'>
                <label className='events-image-label'>Imagem da Detecção</label>
                {anprEvent.detectionImagePath ? (
                  <div className='events-image-preview' onClick={() => props.setSelectedImage(anprEvent.detectionImagePath)}>
                    <Image src={anprEvent.detectionImagePath} alt='Detecção' className='events-preview-image' width='200px' />
                    <div className='events-preview-overlay'>
                      <i className='pi pi-search-plus' />
                      <span>Clique para ampliar</span>
                    </div>
                  </div>
                ) : (
                  <div className='events-no-image-large'>
                    <i className='pi pi-image' />
                    <span>Imagem não disponível</span>
                  </div>
                )}
              </div>
            </div>
          </div>

        </div>
      </div>
    </Dialog>
  )
}

export default EventDetailViewer